import { useState } from "react";
import { formatDateValue } from "../../utils/date";
import { RecordLink } from "../../blocks/layout/RecordLink";
import { PageSection } from "../../blocks/layout/PageSection";
import { Button } from "../../elements/Button";
import { PageBarButtonClass } from "../pageBarButtonClass";
import { ConfirmationModal } from "../../elements/ConfirmationModal";
import { Loading } from "../../elements/Loading";
import { RotateIcon } from "../../icons";
import { DashboardStatus } from "./automationDashboardModel";
import { DashboardExecution, DashboardMetric } from "./automationDashboard.types";
import { RecentRunCards, UpcomingExecutionCards } from "./ExecutionCards";
import { useAutomationDashboard } from "./useAutomationDashboard";

// The page is already a set of cards, so a section does not draw one around them.
const NoCard = "bg-transparent shadow-none rounded-0 p-0";

const RunStatuses = [
  { label: "All", value: "" },
  { label: "Completed", value: "Completed" },
  { label: "Expired", value: "Expired" },
];

function MetricCard({ metric }: { metric: DashboardMetric }) {
  return (
    <div className="col-12 col-md-6 col-xl-3 d-flex">
      <div className="card w-100">
        <div className="card-body">
          <h3 className="fs-6 text-uppercase mb-3">{metric.label}</h3>
          <p className="fw-semibold mb-0">
            <RecordLink
              href={metric.link?.to}
              text={<span className="fs-3">{metric.value}</span>}
              newTab="button"
            />
          </p>
          {metric.meta?.map((item) => (
            <p key={item.value} className="mt-3 mb-0">
              {item.value}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Trust metrics, recent runs and the upcoming steps, from one dashboard read. */
export function AutomationDashboardPanel({ enableAutoRefresh = true }) {
  const {
    dashboard,
    errorMessage,
    isRefreshing,
    isFilteringRuns,
    recentRunStatus,
    cancellingExecutionId,
    preparingExecutionId,
    setRecentRunStatus,
    refresh,
    cancelExecution,
    prepareCommunication,
  } = useAutomationDashboard(enableAutoRefresh);
  const [pendingCancel, setPendingCancel] = useState<DashboardExecution | null>(null);

  const confirmCancel = async () => {
    await cancelExecution(pendingCancel?.id);
    setPendingCancel(null);
  };

  const upcoming = (executions: DashboardExecution[], emptyMessage: string) => (
    <UpcomingExecutionCards
      executions={executions}
      emptyMessage={emptyMessage}
      cancellingExecutionId={cancellingExecutionId}
      preparingExecutionId={preparingExecutionId}
      onCancelClick={setPendingCancel}
      onPrepareCommunication={prepareCommunication}
      onAfterAction={() => refresh(false)}
    />
  );

  const lastUpdated =
    formatDateValue(dashboard.lastUpdated, "DD/MM/YYYY HH:mm:ss") || "Not yet retrieved";

  return (
    <div className="d-flex flex-column gap-5">
      <div className="d-flex flex-wrap align-items-center justify-content-end gap-3">
        <p className="mb-0 text-muted">
          <strong>Last updated:</strong> {lastUpdated}
        </p>
        <Button
          variant="none"
          className={PageBarButtonClass}
          icon={RotateIcon}
          text="Refresh"
          showSpinner={isRefreshing}
          onClick={() => refresh(true)}
        />
      </div>

      {dashboard.status === DashboardStatus.Loading && <Loading />}
      {dashboard.status === DashboardStatus.Error && (
        <div className="alert alert-danger mb-0" role="alert">
          <h3 className="alert-heading h6">Dashboard metrics failed to load</h3>
          <p className="mb-0">{errorMessage}</p>
        </div>
      )}
      {dashboard.status !== DashboardStatus.Loading &&
        dashboard.status !== DashboardStatus.Error && (
          <>
            <div className="row g-3">
              {dashboard.trustMetrics.map((metric) => (
                <MetricCard key={metric.id} metric={metric} />
              ))}
            </div>
            <PageSection
              className={NoCard}
              title="Recent automation runs"
              actions={
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0" htmlFor="automation-run-status">
                    Status
                  </label>
                  <select
                    id="automation-run-status"
                    className="form-select w-auto"
                    value={recentRunStatus}
                    onChange={(event) => setRecentRunStatus(event.target.value)}
                  >
                    {RunStatuses.map((status) => (
                      <option key={status.label} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              }
            >
              <div className="position-relative">
                <RecentRunCards
                runs={dashboard.recentRuns}
                emptyMessage={
                  recentRunStatus
                    ? "No recent automation runs match the selected status."
                    : "Recent automation run data is not yet available for this environment."
                }
              />
                {isFilteringRuns && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-body bg-opacity-50">
                    <Loading />
                  </div>
                )}
              </div>
            </PageSection>
            <PageSection title="Upcoming daily automation steps" className={NoCard}>
              {upcoming(
                dashboard.upcomingDailyScheduledExecutions,
                "No upcoming daily automation steps are currently planned."
              )}
            </PageSection>
            <PageSection title="Upcoming fixture automation steps" className={NoCard}>
              {upcoming(
                dashboard.upcomingFixtureScheduledExecutions,
                "No upcoming fixture automation steps are currently planned."
              )}
            </PageSection>
          </>
        )}

      {pendingCancel && (
        <ConfirmationModal
          modalTitle="Cancel automation send"
          confirmText="Cancel send"
          isSubmitting={cancellingExecutionId === pendingCancel.id}
          onClose={() => setPendingCancel(null)}
          onConfirm={confirmCancel}
        >
          <p className="mb-0">Are you sure you want to cancel this automation send?</p>
        </ConfirmationModal>
      )}
    </div>
  );
}
